import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { mergeSx } from "src/utils/styles.ts";
import { SxProps, Theme } from "@mui/material/styles";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import type { PluggableList } from "unified";

export interface AavoMarkdownProps extends React.ComponentProps<typeof Markdown> {
	sx?: SxProps<Theme>;
	remarkPlugins?: PluggableList;
	rehypePlugins?: PluggableList;
}

export const AavoMarkdown = ({ sx, components, remarkPlugins, rehypePlugins, ...other }: AavoMarkdownProps) => {
	return (
		<VerticalBox
			sx={mergeSx(
				{
					flex: 1,
					alignItems: "stretch",
					"& code": {
						backgroundColor: "#f5f5f5",
						padding: "2px 6px",
						borderRadius: "4px",
						fontSize: "0.9em",
						fontFamily: "monospace",
					},
					"& pre code": {
						backgroundColor: "transparent",
						padding: 0,
					},
				},
				sx,
			)}
			className={"aavo-markdown-wrapper"}
		>
			<Markdown
				remarkPlugins={[remarkGfm, ...(remarkPlugins ?? [])]}
				rehypePlugins={rehypePlugins}
				components={{
					code: AavoMarkdownCode,
					...components,
				}}
				{...other}
			/>
		</VerticalBox>
	);
};

// Module-level so react-markdown keeps a stable component type and does not remount code blocks on re-render.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function AavoMarkdownCode({ inline, className, children, node, ...props }: any) {
	const match = /language-(\w+)/.exec(className || "");
	const language = match ? match[1] : "";

	return !inline && language ?
			<SyntaxHighlighter style={oneLight as any} language={language} PreTag="div" {...props}>
				{String(children).replace(/\n$/, "")}
			</SyntaxHighlighter>
		:	<code className={className} {...props}>
				{children}
			</code>;
}
